/**
 * Commentary booth — generates timed requests ("Get his numbers up", "Show me
 * the gaffer") at sensible moments, mostly stoppages, in a natural commentator
 * voice. The grader resolves them (hit / late / missed) by watching the
 * director; wire `grader opts.onBoothResolved` to `booth.resolve`.
 */
import type {
  CommentatorRequest, MatchConfig, MatchEvent, MatchEventType,
  PlayerProfile, Rng, TeamSide, WorldSnapshot,
} from '../core/contracts';
import type { BoothStatus, Difficulty } from './grader';

export interface BoothOptions { difficulty?: Difficulty }

export interface Booth {
  /** Call every tick (or at least a few times a second). Returns newly created requests. */
  update(snapshot: WorldSnapshot, newEvents: MatchEvent[], t: number): CommentatorRequest[];
  /** Unresolved, unexpired requests. */
  active(t: number): CommentatorRequest[];
  /** Unresolved requests still inside the late-credit window (feed these to the grader). */
  open(t: number, lateWindow?: number): CommentatorRequest[];
  /** Mark a request resolved (from the grader). */
  resolve(id: number, status: BoothStatus): void;
  /** Every request created so far. */
  all(): readonly CommentatorRequest[];
}

type ReqKind = 'stat' | 'replay' | 'gaffer' | 'possession' | 'lineups' | 'crowd' | 'stats' | 'name' | 'tight';

interface Ctx { p: string; team: string; m: string; crowdTime: string }
interface Template { kind: ReqKind; text: (c: Ctx) => string; /** Only after the player struck the ball. */ strikeOnly?: boolean }

/** 30 templates across 9 request kinds. */
export const BOOTH_TEMPLATES: readonly Template[] = [
  // stat card (player_stat_card + subjectId)
  { kind: 'stat', strikeOnly: true, text: (c) => `Lovely strike from ${c.p} — can we get his numbers up?` },
  { kind: 'stat', text: (c) => `${c.p} is having a game. Let's have a look at his stats.` },
  { kind: 'stat', text: (c) => `Get ${c.p}'s season numbers up for me, would you?` },
  { kind: 'stat', text: (c) => `Remind us what ${c.p} has done this season?` },
  { kind: 'stat', text: (c) => `${c.p} again! Can we see his figures?` },
  // replay
  { kind: 'replay', text: () => `Let's see that again.` },
  { kind: 'replay', text: () => `Can we have another look at that?` },
  { kind: 'replay', text: () => `Ooh — show us that one more time.` },
  { kind: 'replay', text: () => `Was there contact? Let's have the replay.` },
  { kind: 'replay', text: () => `Have we got another angle on that?` },
  // gaffer (bench cam)
  { kind: 'gaffer', text: (c) => `Show me the gaffer — ${c.m} won't be happy with that.` },
  { kind: 'gaffer', text: (c) => `How's ${c.m} taking that? Let's see the dugout.` },
  { kind: 'gaffer', text: (c) => `Let's get a look at ${c.m} on the touchline.` },
  { kind: 'gaffer', text: (c) => `${c.m} is out of his seat! Show us the bench.` },
  // possession bar
  { kind: 'possession', text: () => `How are we doing on possession?` },
  { kind: 'possession', text: () => `Who's seeing more of the ball? Get the possession up.` },
  { kind: 'possession', text: (c) => `${c.team} have had plenty of it — let's see the possession numbers.` },
  // lineups (pre-match)
  { kind: 'lineups', text: () => `Lineups when you can, please.` },
  { kind: 'lineups', text: () => `Let's run through the teams before we get going.` },
  { kind: 'lineups', text: () => `Can we get the starting elevens up?` },
  // crowd cam
  { kind: 'crowd', text: (c) => `Crowd's in fine voice ${c.crowdTime} — let's see them!` },
  { kind: 'crowd', text: () => `Listen to this place! Show us the fans.` },
  { kind: 'crowd', text: (c) => `The ${c.team} end are bouncing — give us a crowd shot.` },
  // match stats
  { kind: 'stats', text: () => `Get the stats up — how have the chances been shared?` },
  { kind: 'stats', text: () => `Let's have a look at the match stats.` },
  { kind: 'stats', text: () => `Shots, corners, the lot — can we get the numbers up?` },
  // player name lower third
  { kind: 'name', text: () => `Who was that? Get his name up.` },
  { kind: 'name', text: (c) => `Fresh legs for ${c.team} — who's coming on?` },
  // tight on a player (main tight)
  { kind: 'tight', text: (c) => `Stay with ${c.p} — get us in tight!` },
  { kind: 'tight', text: (c) => `Let's get in close on ${c.p}. What a moment for him.` },
];

const WANTS: Record<Exclude<ReqKind, 'stat' | 'name' | 'tight'>, CommentatorRequest['wants']> = {
  replay: { replay: true },
  gaffer: { camKind: 'bench' },
  possession: { graphic: 'possession_bar' },
  lineups: { graphic: 'team_lineup' },
  crowd: { camKind: 'crowd' },
  stats: { graphic: 'match_stats' },
};

const MAX_ACTIVE: Record<Difficulty, number> = { rookie: 1, pro: 1, legend: 2 };
const COOLDOWN: Record<Difficulty, number> = { rookie: 40, pro: 28, legend: 18 };
const STOPPAGE_LIKE = new Set(['stoppage', 'goal_celebration', 'half_time', 'pre_match']);
const MEMORABLE: ReadonlySet<MatchEventType> = new Set<MatchEventType>([
  'goal', 'shot_on_target', 'save', 'near_miss', 'woodwork', 'foul', 'yellow_card', 'red_card',
  'substitution', 'manager_reaction', 'crowd_moment', 'shot', 'injury', 'var_check', 'penalty',
]);

interface Candidate { kind: ReqKind; weight: number; ev?: MatchEvent; subjectId?: string; team?: TeamSide }

const titleCase = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

export function createBooth(config: MatchConfig, rng: Rng, opts: BoothOptions = {}): Booth {
  const difficulty = opts.difficulty ?? 'pro';
  const maxActive = MAX_ACTIVE[difficulty];
  // Scale cooldown to match length (designed around ~4 min halves).
  const cooldown = COOLDOWN[difficulty] * Math.max(0.6, Math.min(1.4, config.halfLengthSeconds / 240));
  const players = new Map<string, PlayerProfile>();
  for (const team of [config.home, config.away]) for (const p of [...team.players, ...team.bench]) players.set(p.id, p);

  const requests: CommentatorRequest[] = [];
  const resolved = new Map<number, BoothStatus>();
  const recent: MatchEvent[] = [];
  /** Players no longer on the pitch (sent off / subbed off): never the subject of a stat card or close-up. */
  const gone = new Set<string>();
  const used = new Set<number>();
  let nextId = 1;
  let nextAllowedT = 4;
  let lastT: number | null = null;
  let lastKind: ReqKind | null = null;
  let lineupsAsked = false;

  const nameOf = (id: string | null | undefined) => {
    const p = id ? players.get(id) : undefined;
    return p ? titleCase(p.shortName) : 'him';
  };
  const teamOf = (side: TeamSide | null | undefined) => (side === 'away' ? config.away : config.home);

  const isActive = (r: CommentatorRequest, t: number) => !resolved.has(r.id) && !r.resolved && t < r.expiresAt;

  function candidates(s: WorldSnapshot, t: number): Candidate[] {
    const c: Candidate[] = [];
    if (s.phase === 'pre_match') {
      if (!lineupsAsked) c.push({ kind: 'lineups', weight: 10 });
      c.push({ kind: 'crowd', weight: 1, team: 'home' });
      return c;
    }
    const stoppage = STOPPAGE_LIKE.has(s.phase) || (s.phase === 'set_piece' && s.intensity < 0.4);
    for (const ev of recent) {
      if (used.has(ev.id) || t - ev.t > 30 || t - ev.t < 2) continue;
      const w = 1 + ev.importance * 3;
      switch (ev.type) {
        case 'goal':
          if (t - ev.t < 5) break; // let the celebration breathe
          c.push({ kind: 'stat', weight: w * 1.5, ev, subjectId: ev.playerId ?? undefined });
          c.push({ kind: 'replay', weight: w, ev });
          c.push({ kind: 'gaffer', weight: w * 0.8, ev, team: ev.team === 'home' ? 'away' : 'home' });
          c.push({ kind: 'crowd', weight: w * 0.6, ev, team: ev.team ?? 'home' });
          break;
        case 'shot_on_target': case 'save': case 'near_miss': case 'woodwork': case 'shot':
          if (ev.importance < 0.35) break;
          c.push({ kind: 'replay', weight: w * 1.2, ev });
          c.push({ kind: 'stat', weight: w, ev, subjectId: (ev.type === 'save' ? ev.secondaryPlayerId : ev.playerId) ?? ev.playerId ?? undefined });
          break;
        case 'foul': case 'penalty': case 'var_check':
          if (ev.importance >= 0.3) c.push({ kind: 'replay', weight: w, ev });
          break;
        case 'yellow_card': case 'red_card':
          c.push({ kind: 'gaffer', weight: w * 1.2, ev, team: ev.team ?? 'home' });
          c.push({ kind: 'replay', weight: w, ev });
          break;
        case 'manager_reaction':
          c.push({ kind: 'gaffer', weight: w * 1.5, ev, team: ev.team ?? 'home' });
          break;
        case 'crowd_moment':
          c.push({ kind: 'crowd', weight: w, ev, team: ev.team ?? 'home' });
          break;
        case 'substitution':
          if (ev.playerId) c.push({ kind: 'name', weight: w * 1.3, ev, subjectId: ev.playerId, team: ev.team ?? 'home' });
          break;
        case 'injury':
          c.push({ kind: 'gaffer', weight: w * 0.7, ev, team: ev.team ?? 'home' });
          break;
        default: break;
      }
    }
    // Celebration close-up requests while the goal_celebration is on.
    if (s.phase === 'goal_celebration') {
      const goal = recent.find((e) => e.type === 'goal' && !used.has(e.id) && t - e.t < 8 && t - e.t > 1.5);
      if (goal?.playerId) c.push({ kind: 'tight', weight: 3, ev: goal, subjectId: goal.playerId });
    }
    if (stoppage) {
      const minute = s.matchClock / 60;
      if (minute > 10) c.push({ kind: 'stats', weight: 0.8 });
      c.push({ kind: 'possession', weight: 0.8, team: s.possession ?? 'home' });
      c.push({ kind: 'crowd', weight: 0.4, team: s.possession ?? 'home' });
    } else if (s.phase === 'open_play' && s.intensity < 0.25) {
      c.push({ kind: 'possession', weight: 0.5, team: s.possession ?? 'home' });
    }
    return c.filter((x) => x.kind !== lastKind || x.ev !== undefined);
  }

  function create(cand: Candidate, t: number): CommentatorRequest | null {
    const pool = BOOTH_TEMPLATES.filter((tp) => tp.kind === cand.kind);
    if (pool.length === 0) return null;
    const subject = cand.subjectId;
    if (subject && (cand.kind === 'stat' || cand.kind === 'tight') && gone.has(subject)) return null;
    let wants: CommentatorRequest['wants'];
    switch (cand.kind) {
      case 'stat': if (!subject) return null; wants = { graphic: 'player_stat_card', subjectId: subject }; break;
      // Name requests only follow substitutions: the sub lower third (Q) on the player coming on answers both.
      case 'name': if (!subject) return null; wants = { graphic: 'sub_lower_third', subjectId: subject }; break;
      case 'tight': wants = { camKind: 'main_tight' }; break;
      default: wants = { ...WANTS[cand.kind] };
    }
    // "Lovely strike" reads oddly for a keeper's save — only use it after a shot by the subject.
    const struck = !!cand.ev && ['goal', 'shot', 'shot_on_target', 'woodwork', 'near_miss'].includes(cand.ev.type) && cand.ev.playerId === subject;
    const templates = pool.filter((tp) => !tp.strikeOnly || struck);
    const team = teamOf(cand.team ?? cand.ev?.team);
    const ctx: Ctx = {
      p: nameOf(subject ?? cand.ev?.playerId),
      team: team.name,
      m: team.manager,
      crowdTime: config.timeOfDay === 'night' ? 'tonight' : 'this afternoon',
    };
    const expiry = rng.range(8, 12) + (difficulty === 'rookie' ? 1 : difficulty === 'legend' ? -0.5 : 0);
    const req: CommentatorRequest = {
      id: nextId++, t, text: rng.pick(templates).text(ctx), wants, expiresAt: t + Math.min(12, Math.max(8, expiry)),
    };
    if (cand.ev) used.add(cand.ev.id);
    if (cand.kind === 'lineups') lineupsAsked = true;
    lastKind = cand.kind;
    requests.push(req);
    return req;
  }

  return {
    update(snapshot, newEvents, t) {
      const dt = lastT === null ? 0 : Math.max(0, t - lastT);
      lastT = t;
      for (const ev of newEvents) {
        if (MEMORABLE.has(ev.type)) recent.push(ev);
        if (ev.type === 'red_card' && ev.playerId) gone.add(ev.playerId);
        if (ev.type === 'substitution' && ev.secondaryPlayerId) gone.add(ev.secondaryPlayerId);
      }
      while (recent.length > 0 && t - recent[0]!.t > 40) recent.shift();
      if (snapshot.phase === 'full_time') return [];
      const activeCount = requests.filter((r) => isActive(r, t)).length;
      if (activeCount >= maxActive || t < nextAllowedT || dt === 0) return [];
      // Never interrupt dangerous live play.
      if ((snapshot.phase === 'open_play' || snapshot.phase === 'kickoff') && snapshot.intensity >= 0.25) return [];
      const cands = candidates(snapshot, t);
      if (cands.length === 0) return [];
      const contextual = cands.some((c) => c.ev !== undefined || c.kind === 'lineups');
      const hazard = snapshot.phase === 'pre_match' ? 0.5 : contextual ? 0.45 : 0.06;
      if (!rng.chance(1 - Math.exp(-hazard * dt))) return [];
      let sum = 0;
      for (const c of cands) sum += c.weight;
      let roll = rng.next() * sum;
      let pick = cands[cands.length - 1]!;
      for (const c of cands) { roll -= c.weight; if (roll <= 0) { pick = c; break; } }
      const req = create(pick, t);
      if (!req) return [];
      nextAllowedT = t + cooldown * rng.range(0.7, 1.3);
      return [req];
    },
    active: (t) => requests.filter((r) => isActive(r, t)),
    open: (t, lateWindow = 6) => requests.filter((r) => !resolved.has(r.id) && !r.resolved && t < r.expiresAt + lateWindow),
    resolve(id, status) { resolved.set(id, status); },
    all: () => requests,
  };
}
